import React from 'react';
import { CONCURRENT_TASK_TYPES, ConcurrentTaskType, UITask } from './types';
import { isTaskInProgress, isTaskQueued } from '@/utils/taskUtils';
import { MAX_CONCURRENT_DEFAULT } from './uiTask';

export type GetActiveServerTasksByType = (taskType: ConcurrentTaskType) => unknown[];
export type GetDispatchedPendingCountByType = (taskType: ConcurrentTaskType) => number;
export type GetConcurrencyLimits = (
	taskType: ConcurrentTaskType,
	maxConcurrent?: number,
) => {
	maxConcurrent: number;
	serverSideActive: number;
	clientSideActive: number;
	totalLoad: number;
	availableSlots: number;
	canAcceptMore: boolean;
};

/**
 * Creates a getter for active server tasks based on task type.
 * Keep minimal knowledge in the provider and centralize the branching here.
 */
export function createGetActiveServerTasksByType(deps: {
	uploadInProgressTasks: unknown[];
	deleteInProgressTasks: unknown[];
	updateInProgressTasks?: unknown[];
}): GetActiveServerTasksByType {
	const { uploadInProgressTasks, deleteInProgressTasks, updateInProgressTasks = [] } = deps;
	return (taskType: ConcurrentTaskType) => {
		if (taskType === CONCURRENT_TASK_TYPES.UPLOAD_RECORD) return uploadInProgressTasks;
		if (taskType === CONCURRENT_TASK_TYPES.DELETE_RECORD) return deleteInProgressTasks;
		if (taskType === CONCURRENT_TASK_TYPES.UPDATE_RECORD) return updateInProgressTasks;
		return [];
	};
}

/**
 * Counts tasks that were dispatched but UI still shows as queued.
 * They should count toward concurrency to avoid overscheduling.
 *
 * Status comes from the live task list. globalTasks holds the snapshot each task was scheduled
 * with, where a started task still reads as queued — counting that as pending would charge it to
 * the limit a second time, on top of the in-progress count it already contributes.
 */
export function createGetDispatchedPendingCountByType(deps: {
	runtime: { marks: { dispatched: Set<string> } };
	globalTasksRef: React.MutableRefObject<Map<string, UITask>>;
	uiTasksRef: React.MutableRefObject<UITask[]>;
}): GetDispatchedPendingCountByType {
	const { runtime, globalTasksRef, uiTasksRef } = deps;
	return (taskType: ConcurrentTaskType) => {
		let pending = 0;
		runtime.marks.dispatched.forEach((id) => {
			const uiTask = globalTasksRef.current.get(id);
			if (!uiTask) return;
			if (uiTask.taskType !== taskType) return;
			const liveTask = uiTasksRef.current.find((task) => task.id === id);
			const apiTask = (liveTask ?? uiTask).execution.apiTask;
			if (isTaskQueued(apiTask)) pending++;
		});
		return pending;
	};
}

/**
 * Calculates concurrency limits combining server-side load, client in-progress,
 * and dispatched-but-not-yet-reflected tasks.
 */
export function createGetConcurrencyLimits(deps: {
	getActiveServerTasksByType: GetActiveServerTasksByType;
	uiTasksRef: React.MutableRefObject<UITask[]> | { current?: never } | UITask[];
	getDispatchedPendingCountByType: GetDispatchedPendingCountByType;
}): GetConcurrencyLimits {
	const { getActiveServerTasksByType, uiTasksRef, getDispatchedPendingCountByType } = deps;
	return (taskType: ConcurrentTaskType, maxConcurrent: number = MAX_CONCURRENT_DEFAULT) => {
		const uiTasks = Array.isArray(uiTasksRef)
			? uiTasksRef
			: (uiTasksRef as React.MutableRefObject<UITask[]>).current;
		const serverSideActive = getActiveServerTasksByType(taskType).length;
		const clientSideActive = uiTasks.filter(
			(task) => task.taskType === taskType && isTaskInProgress(task.execution.apiTask),
		).length;
		const dispatchedPending = getDispatchedPendingCountByType(taskType);
		const totalLoad = serverSideActive + clientSideActive + dispatchedPending;
		const availableSlots = Math.max(0, maxConcurrent - totalLoad);
		return {
			maxConcurrent,
			serverSideActive,
			clientSideActive,
			totalLoad,
			availableSlots,
			canAcceptMore: totalLoad < maxConcurrent,
		};
	};
}
